
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface ClubCoach {
  id: string;
  club_id: string;
  coach_id: string;
  created_at: string;
  title?: string;
  phone?: string;
  email?: string;
  bio?: string;
  profile_picture_url?: string;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export interface Coach {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface UpdateClubCoachData {
  title?: string;
  phone?: string;
  email?: string;
  bio?: string;
  profile_picture_url?: string;
}

export const useClubCoaches = () => {
  const [clubCoaches, setClubCoaches] = useState<ClubCoach[]>([]);
  const [availableCoaches, setAvailableCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const fetchClubCoaches = useCallback(async (clubId: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('club_coaches')
        .select(`
          *,
          profiles!inner(first_name, last_name, email)
        `)
        .eq('club_id', clubId)
        .order('created_at');

      if (error) {
        console.error('Error fetching club coaches:', error);
        throw error;
      }
      
      setClubCoaches(data || []);
    } catch (error) {
      console.error('Error fetching club coaches:', error);
      toast.error('Failed to load club coaches');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableCoaches = async () => {
    try {
      
      // Since we no longer have 'coach' role, we'll fetch all members
      // In a real system, you might want to track coach status differently
      // For now, we'll fetch all members who could potentially be coaches
      const { data, error } = await supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .eq('role', 'member')
        .order('first_name');

      if (error) {
        console.error('Error fetching available coaches:', error);
        throw error;
      }
      
      setAvailableCoaches(data || []);
    } catch (error) {
      console.error('Error fetching available coaches:', error);
      toast.error('Failed to load available coaches');
    }
  };

  const addCoachToClub = async (clubId: string, coachId: string) => {
    try {
      
      const { error } = await supabase
        .from('club_coaches')
        .insert([{ club_id: clubId, coach_id: coachId }]);

      if (error) {
        console.error('Error adding coach to club:', error);
        throw error;
      }
      
      toast.success('Coach added to club successfully');
      await fetchClubCoaches(clubId);
    } catch (error) {
      console.error('Error adding coach to club:', error);
      toast.error('Failed to add coach to club');
    }
  };

  const updateClubCoach = async (clubCoachId: string, updateData: UpdateClubCoachData) => {
    try {
      
      const { error } = await supabase
        .from('club_coaches')
        .update(updateData)
        .eq('id', clubCoachId);

      if (error) {
        console.error('Error updating coach information:', error);
        throw error;
      }
      
      toast.success('Coach information updated successfully');
      
      // Refresh the coach data for the current club
      const currentCoach = clubCoaches.find(cc => cc.id === clubCoachId);
      if (currentCoach) {
        await fetchClubCoaches(currentCoach.club_id);
      }
    } catch (error) {
      console.error('Error updating coach information:', error);
      toast.error('Failed to update coach information');
    }
  };

  const removeCoachFromClub = async (clubCoachId: string, clubId: string) => {
    try {
      setRemoving(clubCoachId);
      
      // Step 1: Get the club coach record
      const { data: clubCoachRecord, error: fetchError } = await supabase
        .from('club_coaches')
        .select('*')
        .eq('id', clubCoachId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      if (!clubCoachRecord) {
        throw new Error('Coach record not found');
      }


      // Step 2: Get coach name for success message
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('first_name, last_name')
        .eq('id', clubCoachRecord.coach_id)
        .single();

      if (profileError) {
        throw profileError;
      }

      const coachName = `${profileData.first_name} ${profileData.last_name}`;

      // Step 3: Remove coach from team_coaches table for this club's teams
      
      // First get the team IDs for this club
      const { data: clubTeams, error: clubTeamsError } = await supabase
        .from('teams')
        .select('id')
        .eq('club_id', clubId);

      if (clubTeamsError) {
        throw clubTeamsError;
      }

      const teamIds = clubTeams?.map(team => team.id) || [];
      
      if (teamIds.length > 0) {
        const { error: teamCoachesError } = await supabase
          .from('team_coaches')
          .update({ is_active: false })
          .eq('coach_id', clubCoachRecord.coach_id)
          .in('team_id', teamIds);

        if (teamCoachesError) {
          throw teamCoachesError;
        }

      }

      // Step 4: Delete the club_coaches record using ONLY the primary key
      
      const { error: deleteError, count } = await supabase
        .from('club_coaches')
        .delete({ count: 'exact' })
        .eq('id', clubCoachId);


      if (deleteError) {
        throw deleteError;
      }

      if (count === 0) {
        // Let's check if the record still exists
        const { data: checkData } = await supabase
          .from('club_coaches')
          .select('*')
          .eq('id', clubCoachId);
        throw new Error('No records were deleted - record may not exist or permissions issue');
      }

      
      // Show success message
      toast.success(`${coachName} removed from club successfully`);
      
      // Refresh data
      await fetchClubCoaches(clubId);
      
    } catch (error) {
      console.error('Coach removal failed:', error);
      if (error instanceof Error) {
        toast.error(`Failed to remove coach: ${error.message}`);
      } else {
        toast.error('Failed to remove coach from club. Please try again.');
      }
    } finally {
      setRemoving(null);
    }
  };

  useEffect(() => {
    fetchAvailableCoaches();
  }, []);

  return {
    clubCoaches,
    availableCoaches,
    loading,
    removing,
    fetchClubCoaches,
    addCoachToClub,
    updateClubCoach,
    removeCoachFromClub
  };
};
